import type { KyInstance } from 'ky';

import { ensureError, isNotDefined } from '@enterprise/core';
import type { OperationResult } from '@enterprise/types';

import { Uri } from '#constants/uri';

/**
 * Deletes scoring tags from the specified mailing lists by making a DELETE request
 * to the mailing list service.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param mailingListIds - An array of mailing list IDs from which to delete scoring tags.
 * @returns A promise that resolves to the result of the delete operation.
 * @throws Throws an error if the request fails, if mailingListIds is not provided,
 *         or if an unexpected error occurs.
 */
export const deleteMailingListScoringTags = async (instance: KyInstance, mailingListIds?: number[]) => {
  try {
    if (isNotDefined(mailingListIds) || mailingListIds.length === 0) {
      throw new Error('Mailing List IDs must be provided to delete mailing list scoring tags.');
    }

    const searchParams = new URLSearchParams();

    for (const id of mailingListIds) {
      searchParams.append('mailingListIds', id.toString());
    }

    return await instance
      .delete<OperationResult>(Uri.mailingList.deleteMailingListScoringTags, {
        searchParams,
      })
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
